import { priceMeta } from './priceCatalog.js';
import { isDedicatedProduct } from './subscriptionLines.js';

/**
 * Translate a resolved checkout plan into a PreviewCheckout request.
 * Plan ops route per existing sub, matching how Stripe Invoices.Upcoming
 * is scoped (one call per subscription).
 *
 * @param {object} plan        — from resolveFullCheckoutUpdates
 * @param {object} catalog     — priceCatalog
 * @param {{ userId: string, coupon?: string, useTrial?: boolean, addsAsNewSubs?: boolean }} ctx
 * @returns {object | null}    — gRPC body, or null if plan is noop
 */
export function buildPreviewRequestFromPlan(plan, catalog, ctx) {
  if (!plan || plan.type !== 'multi-cycle') return null;
  if (!ctx?.userId) return null;

  /** @type {Map<string, Array<object>>} */
  const itemsBySub = new Map();
  const newSubs = [];

  const pushItem = (subId, item) => {
    if (!subId) return;
    if (!itemsBySub.has(subId)) itemsBySub.set(subId, []);
    itemsBySub.get(subId).push(item);
  };

  for (const op of plan.ops) {
    for (const mod of op.modifies || []) {
      pushItem(mod.subscriptionId, {
        itemId: mod.subscriptionItemId || '',
        quantity: Number(mod.quantity) || 0,
        // Variant swap path: planner sets mod.priceId when desired SKU
        // differs from owner's current SKU (e.g. futures default → unlimited).
        // Empty string tells BM "no price change, qty only" per existing contract.
        priceId: mod.priceId || '',
        remove: false
      });
    }

    for (const add of op.adds || []) {
      // Crypto buys every added line as its own full-price plan (planToCryptoRequest), so it is quoted as one.
      const target = ctx.addsAsNewSubs ? '' : add.sub_id || op.subId;
      if (!target) {
        newSubs.push({
          priceId: add.price_id,
          quantity: Number(add.quantity) || 0,
          dedicated: isDedicatedPrice(catalog, add.price_id)
        });
        continue;
      }
      pushItem(target, {
        itemId: '',
        quantity: Number(add.quantity) || 0,
        priceId: add.price_id,
        remove: false
      });
    }

    for (const cr of op.creates || []) {
      newSubs.push({
        priceId: cr.price_id,
        quantity: Number(cr.quantity) || 0,
        dedicated: isDedicatedPrice(catalog, cr.price_id)
      });
    }

    // Line cancellations (e.g. collapsing a legacy line on a seat drop): the
    // remove delta makes the quote match what apply will do.
    for (const rm of op.removes || []) {
      pushItem(rm.subscriptionId || op.subId, {
        itemId: rm.subscriptionItemId || '',
        quantity: 0,
        priceId: '',
        remove: true
      });
    }
  }

  const updates = Array.from(itemsBySub.entries()).map(([subscriptionId, items]) => ({ subscriptionId, itemsList: items }));

  if (updates.length === 0 && newSubs.length === 0) return null;

  return {
    userId: ctx.userId,
    coupon: ctx.coupon || '',
    useTrial: !!ctx.useTrial,
    updatesList: updates,
    newSubsList: newSubs
  };
}

function isDedicatedPrice(catalog, priceId) {
  const meta = priceMeta(catalog?.getById?.(priceId));
  // Both DE kinds set the new-sub `dedicated` flag so BM provisions the env.
  return isDedicatedProduct(meta?.product);
}
